import {
    type ReactNode,
    useEffect,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    ScrollView,
    Platform,
    View
} from "react-native";
import type IPopoverContentProps from "./type";
import stylesheet, {
    POPOVER_CONTENT_DESCRIPTION_VARIANT,
    POPOVER_CONTENT_TITLE_VARIANT,
    getPopoverContentWidth,
    useStyles
} from "./stylesheet";
import {
    getPopoverContentFooterActions,
    splitPopoverContentChildren
} from "./util";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    PopoverLayoutContext,
    usePopoverContext,
    PopoverContext
} from "../../context";
import type {
    PopoverSurfaceWebPropsType,
    PopoverKeyboardEventType
} from "../../type";
import {
    POPOVER_NO_COLLISION_PADDING,
    handlePopoverTabKeyDown
} from "../../util";
import {
    warnOnce
} from "../../../../utils/development";
import ActionSheet from "../../../bottomSheet/components/actionSheet";
import PopoverSurface from "../../../popoverSurface";
import PopoverClose from "../close";
import Text from "../../../text";

const PopoverContent: FC<IPopoverContentProps> = ({
    customTheme: customThemeProp,
    "aria-label": ariaLabel,
    isShowClose = true,
    initialFocusRef,
    customLocalize,
    description,
    isLoading,
    children,
    title,
    style
}) => {
    const context = usePopoverContext({
        part: "Content"
    });

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        shapeConfigs,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const baseId = useId();

    const surfaceRef = useRef(null);

    const titleId = `${baseId}-title`;

    const descriptionId = `${baseId}-description`;

    const {
        bodyContent: bodyContentDynamicStyle,
        headerText: headerTextDynamicStyle,
        sheetBody: sheetBodyDynamicStyle,
        content: contentDynamicStyle,
        header: headerDynamicStyle,
        body: bodyDynamicStyle
    } = useStyles({
        borders,
        colors,
        spaces
    });

    const layout = useMemo(() => ({
        isSheet: context?.touchBehaviour === "bottomSheet" && pointerType === "coarse"
    }), [
        context?.touchBehaviour,
        pointerType
    ]);

    useEffect(() => {
        if(title || ariaLabel) {
            return;
        }

        warnOnce({
            message: "Popover.Content: a \"title\" or an \"aria-label\" is needed so screen readers can name the dialog."
        });
    }, [
        ariaLabel,
        title
    ]);

    if(!context) {
        return null;
    }

    const {
        isMatchAnchorWidth = true,
        isAvoidCollisions,
        handleClose,
        isAnchored,
        triggerRef,
        anchorRef,
        contentId,
        isModal,
        isOpen,
        align,
        side,
        size
    } = context;

    const {
        footers,
        body
    } = splitPopoverContentChildren(children);

    const renderProviders = (node: ReactNode) => {
        return <PopoverContext.Provider
            value={context}
        >
            <PopoverLayoutContext.Provider
                value={layout}
            >
                {node}
            </PopoverLayoutContext.Provider>
        </PopoverContext.Provider>;
    };

    const onKeyDown = (event: PopoverKeyboardEventType) => {
        handlePopoverTabKeyDown({
            onLeave: handleClose,
            surfaceRef,
            triggerRef,
            event
        });
    };

    const renderSheet = () => {
        const [
            footer
        ] = footers;

        const footerActions = getPopoverContentFooterActions({
            footerAlign: footer?.props.align ?? "end",
            footerNodes: footer?.props.children,
            onClose: handleClose
        });

        return <ActionSheet
            customLocalize={customLocalize ?? context.customLocalize}
            primaryAction={footerActions.primaryAction}
            action={footerActions.action}
            title={title ?? ariaLabel}
            description={description}
            customTheme={customTheme}
            onClosed={handleClose}
            isActive={isOpen}
        >
            {
                renderProviders(<View
                    style={[
                        stylesheet.sheetBody,
                        sheetBodyDynamicStyle
                    ]}
                    aria-busy={isLoading}
                >
                    {body}
                </View>)
            }
        </ActionSheet>;
    };

    const renderHeader = () => {
        if(!title && !description && !isShowClose) {
            return null;
        }

        return <View
            style={[
                stylesheet.header,
                headerDynamicStyle
            ]}
        >
            <View
                style={[
                    stylesheet.headerText,
                    headerTextDynamicStyle
                ]}
            >
                {
                    title ?
                        <Text
                            variant={POPOVER_CONTENT_TITLE_VARIANT}
                            customTheme={customTheme}
                            role="heading"
                            id={titleId}
                            color="high"
                        >
                            {title}
                        </Text>
                    :
                        null
                }
                {
                    description ?
                        <Text
                            variant={POPOVER_CONTENT_DESCRIPTION_VARIANT}
                            customTheme={customTheme}
                            id={descriptionId}
                            color="mid"
                        >
                            {description}
                        </Text>
                    :
                        null
                }
            </View>
            {
                isShowClose ?
                    <PopoverClose
                        customLocalize={customLocalize}
                        customTheme={customTheme}
                    />
                :
                    null
            }
        </View>;
    };

    const renderBody = () => {
        if(!body.length) {
            return null;
        }

        return <ScrollView
            contentContainerStyle={bodyContentDynamicStyle}
            keyboardShouldPersistTaps="handled"
            style={[
                stylesheet.body,
                bodyDynamicStyle
            ]}
        >
            {body}
        </ScrollView>;
    };

    const webProps: PopoverSurfaceWebPropsType = Platform.OS === "web" && !isModal ? {
        onKeyDown
    } : {};

    if(layout.isSheet) {
        return renderSheet();
    }

    return <PopoverSurface
        {...webProps}
        collisionPadding={isAvoidCollisions ? spaces.spacingMd : POPOVER_NO_COLLISION_PADDING}
        aria-describedby={description ? descriptionId : undefined}
        minWidth={isMatchAnchorWidth ? "anchor" : undefined}
        anchorRef={isAnchored ? anchorRef : triggerRef}
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        initialFocusRef={initialFocusRef}
        width={getPopoverContentWidth({
            shapeConfigs,
            size
        })}
        customTheme={customTheme}
        onDismiss={handleClose}
        triggerRef={triggerRef}
        aria-busy={isLoading}
        isAutoFocused={true}
        padding="spacingMd"
        isModal={isModal}
        ref={surfaceRef}
        isOpen={isOpen}
        id={contentId}
        align={align}
        role="dialog"
        style={style}
        side={side}
    >
        {
            renderProviders(<View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderHeader()}
                {renderBody()}
                {footers}
            </View>)
        }
    </PopoverSurface>;
};
export default PopoverContent;
